import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
  Listen,
} from '@stencil/core';
import { generateIdFromString } from '../../utils/generateIdFromString';
import { AhaTreeItem } from '../aha-tree-item/aha-tree-item';

/**
 * @slot default - Contents of the default slot.
 * @slot icon - Contents of the icon slot, otherwise defaults to a type-specific icon.
 * @slot expansion-icon - Contents of the icon for the expanision-icon slot, otherwise defaults to a right angle icon.
 */
@Component({
  tag: 'aha-toc',
  styleUrl: 'aha-toc.css',
  shadow: true,
})
export class AhaToc {
  @Element() host!: HTMLAhaTocElement;

  private treeElem?: HTMLAhaTreeElement;

  // Map of heading elements and their corresponding aha-tree-item elements.
  private headingToTreeItemMap: Map<HTMLElement, AhaTreeItem> = new Map();

  // Map of aha-tree-item elements and their corresponding heading elements.
  private treeItemToHeadingMap: Map<AhaTreeItem, HTMLElement> = new Map();

  // Weak set of heading elements that have had their IDs generated. This prevents user-defined IDs from being overwritten.
  private headingsWithGeneratedIds = new WeakSet<HTMLElement>();

  private selectors = 'h1,h2,h3,h4,h5,h6';

  @Prop({ reflect: true }) open?: boolean;
  @Watch('open')
  openChanged() {
    if (this.open) {
      this.openedEvent.emit({ element: this.host });
    } else {
      this.closedEvent.emit({ element: this.host });
    }
  }

  @Prop({ reflect: true }) lines?: boolean;

  // Watch for new elements being added to the default slot and re-generate the TOC if there are any changed elements that match the selectors.
  private tocMutationObserver = new MutationObserver(() => {
    const matches = this.matchingElements;
    if (matches.length !== this.headingToTreeItemMap.size) {
      // If the number of matches is different, then regenerate the TOC, as we know something has changed.
      this.generateTOC();
    } else {
      // If the number of matches is the same, then check if any of the elements have changed.
      if (matches.find(elem => !this.headingToTreeItemMap.has(elem))) {
        this.generateTOC();
      }
    }
  });

  @Prop({ reflect: true }) ariaDescribedby?: string;
  @Watch('ariaDescribedby')
  handleAriaDescribedbyChange() {
    if (this.treeElem) this.treeElem.innerHTML = '';
    this.tocMutationObserver?.disconnect();
    if (this.headingsRoot) {
      this.generateTOC();
      this.tocMutationObserver.observe(this.headingsRoot, { childList: true, subtree: true });
    }
  }

  // Watch for mutations to the headings and update the corresponding tree items.
  private headingMutationObs = new MutationObserver(entries => {
    entries.forEach(entry => {
      let target = entry.target;
      let elem = entry.target instanceof HTMLElement ? target : target.parentElement;

      if (elem instanceof HTMLElement) {
        const heading = elem.closest(this.selectors);
        if (heading instanceof HTMLElement) {
          const treeItem = this.headingToTreeItemMap.get(heading);

          // Get the matching tree item element and set its internal `<a>` element to have the same text as the matching heading.
          if (treeItem) {
            // Update the element's id if the text content has changed.
            if (entry.type === 'characterData') this.generateId(elem as HTMLElement);

            // Update the tree item's text content and link.
            const link = treeItem.querySelector<HTMLAnchorElement>(':scope a');
            if (link) {
              link.href = `#${elem.id}`;
              link.textContent = entry.target.textContent;
            }
          }
        }
      }
    });
  });

  componentDidLoad() {
    this.handleAriaDescribedbyChange();
  }

  get headingsRoot() {
    return this.ariaDescribedby ? document.getElementById(this.ariaDescribedby) : this.host;
  }

  get matchingElements() {
    const headingsRoot = this.headingsRoot;
    if (!headingsRoot) return [];
    return Array.from(headingsRoot.querySelectorAll<HTMLElement>(`:scope :is(${this.selectors})`));
  }

  private generateTOC() {
    const frag = document.createDocumentFragment();

    this.matchingElements.forEach((elem, index, arr) => {
      const prevElem = arr[index - 1];

      // If the corresponding tree item does not exist, then create it, and watch the original element
      // for text changes that should be reflected back to the tree item.
      const treeItem = document.createElement('aha-tree-item') as AhaTreeItem;
      treeItem.setAttribute('part', 'tree-item');
      const link = document.createElement('a');
      link.setAttribute('part', 'link');
      treeItem.appendChild(link);
      treeItem.level = 3;

      // Link the heading to the aha-tree-item, and the aha-tree-item to the heading.
      this.headingToTreeItemMap.set(elem, treeItem);
      this.treeItemToHeadingMap.set(treeItem, elem);

      link.textContent = elem.textContent;
      this.headingMutationObs.observe(elem, {
        attributes: true,
        attributeFilter: ['id'],
        subtree: true,
        characterData: true,
      });

      // Set the correct `ariaLevel` for the aha-tree-item.
      let depth = Number(elem.tagName.replace(/\D/g, ''));
      if (prevElem) {
        const prevTreeItem = this.headingToTreeItemMap.get(prevElem);
        if (prevTreeItem && depth - prevTreeItem.level > 1) {
          depth = prevTreeItem.level + 1;
        }
      }
      treeItem.level = depth;
      treeItem.removeAttribute('aria-expanded');
      if (treeItem) frag.appendChild(treeItem);

      // Generate an id for the heading element if it does not have one and link the tree item to it.
      this.generateId(elem);
      treeItem.querySelector('a')?.setAttribute('href', `#${elem.id}`);
    });

    const treeElem = this.treeElem;
    if (treeElem) {
      // When the tree is being updated, set the height of the tree element to the current height to prevent a jarring resize.
      // The resize can occur because the aha-tree-item elements are not yet fully mounted (no shadow DOM) and so they have no height.
      treeElem.style.setProperty(
        '--aha-toc--temp-height',
        treeElem.getBoundingClientRect().height + 'px',
      );
      this.treeElem?.replaceChildren(frag);
    }
  }

  @Listen('loaded')
  handleTreeLoaded() {
    // Remove the temporary height property after the tree has loaded.
    this.treeElem?.style.removeProperty('--aha-toc--temp-height');
  }

  // If the element either already has a generated ID or does not have an ID, then generate one.
  // This prevents the element from being re-generated if it already has a user-defined ID.
  private generateId(elem: HTMLElement) {
    if (this.headingsWithGeneratedIds.has(elem) || !elem.id) {
      this.headingsWithGeneratedIds.add(elem);
      const newId = generateIdFromString(elem.textContent || '', {
        addRandomSuffix: true,
        maxLength: 255,
      });
      // Set the new ID if it is different from the current ID.
      // This prevents a mutation observer from firing if the ID is the same.
      if (elem.id !== newId) elem.id = newId;
    }
  }

  private handleSummaryClick = (event: MouseEvent) => {
    event.preventDefault();
    this.open = !this.open;
  };

  @Event({
    eventName: 'opened',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  @Event({
    eventName: 'closed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  render() {
    return (
      <Host role="navigation">
        <details part="details" open={this.open}>
          <summary part="summary" onClick={this.handleSummaryClick}>
            <div id="icon" part="icon">
              <slot name="icon">
                <fa-icon class="fa-regular fa-square-poll-horizontal fa-fw"></fa-icon>
              </slot>
            </div>
            <div id="title" part="title">
              <slot name="title">Table of Contents</slot>
            </div>
            <div id="expansion-icon" part="expansion-icon">
              <slot name="expansion-icon">
                <fa-icon id="default-expansion-icon" class="fa-regular fa-angle-right"></fa-icon>
              </slot>
            </div>
          </summary>
          <aha-tree part="tree" ref={e => (this.treeElem = e)} lines={this.lines}></aha-tree>
        </details>
        <div id="empty-state" aria-hidden="true">
          <slot name="empty-state"></slot>
        </div>
        <slot></slot>
      </Host>
    );
  }
}
